import {
	CONDUCTOR_APP_URL,
} from '~/config';



const REFRESH_AFTER_MS = 14 * 60 * 1000; // 14 minutes
const REFRESH_CHECK_EVERY_MS = 60 * 1000; // 1 minute
const LAST_REFRESH_KEY = 'last_session_refresh';
const CHANNEL_NAME = 'session-refresh';


let refreshInterval: ReturnType<typeof setInterval> | null = null;
let channel: BroadcastChannel | null = null;
let visibilityListener: (() => void) | null = null;
let immediateRefreshTimeout: ReturnType<typeof setTimeout> | null = null;


function _setChannel(): void {
	if (typeof BroadcastChannel === 'undefined') {
		return;
	}

	if (channel !== null) {
		channel.close();
		channel = null;
	}

	channel = new BroadcastChannel(CHANNEL_NAME);

	channel.addEventListener('message', (event) => {
		if (event.data?.type === 'refresh') {
			setLastRefreshTimestamp(event.data.timestamp);
		}
	});
}


function _getLastRefresh(): number | null {
	if (typeof localStorage === 'undefined') {
		return null;
	}

	const stored = localStorage.getItem(LAST_REFRESH_KEY);

	if (stored === null) {
		return null;
	}

	const timestamp = Number.parseInt(stored, 10);

	if (Number.isNaN(timestamp)) {
		return null;
	}

	return timestamp;
}


function _shouldRefresh(): boolean {
	const lastRefresh = _getLastRefresh();
	const now = Date.now();

	return lastRefresh === null || now - lastRefresh >= REFRESH_AFTER_MS;
}


function setLastRefreshTimestamp(timestamp: number): void {
	if (typeof localStorage === 'undefined') {
		return;
	}

	localStorage.setItem(LAST_REFRESH_KEY, String(timestamp));
}


function _resetInterval(): void {
	if (refreshInterval !== null) {
		clearInterval(refreshInterval);
		refreshInterval = null;
	}

	refreshInterval = setInterval(
		() => {
			if (_shouldRefresh()) {
				_refreshToken();
			}
		},
		REFRESH_CHECK_EVERY_MS,
	);
}


function _resetVisibilityListener(): void {
	if (visibilityListener !== null) {
		document.removeEventListener('visibilitychange', visibilityListener);
		visibilityListener = null;
	}

	visibilityListener = () => {
		if (document.hidden) {
			// Continue refreshing while hidden to prevent token expiration
			// Tokens expire regardless of tab visibility, so we must keep refreshing
			// The interval continues running, ensuring tokens stay fresh
		} else {
			// When tab becomes visible, check if refresh is needed immediately
			if (_shouldRefresh()) {
				_refreshToken();
			}

			// Ensure interval is running (it should already be running, but double-check)
			if (refreshInterval === null) {
				_resetInterval();
			}
		}
	};

	document.addEventListener('visibilitychange', visibilityListener);
}


async function _refreshToken(): Promise<void> {
	if (!CONDUCTOR_APP_URL) {
		console.error('[sessionRefreshUtil] CONDUCTOR_APP_URL is not configured');
		return;
	}

	const url = `${CONDUCTOR_APP_URL}/gateway/v1/session/access-token`;

	try {
		const response = await fetch(
			url,
			{
				credentials: 'include',
				method: 'POST',
			},
		);

		if (response.ok) {
			const timestamp = Date.now();
			setLastRefreshTimestamp(timestamp);

			if (channel !== null) {
				channel.postMessage({
					timestamp,
					type: 'refresh',
				});
			}
		} else if (response.status === 401) {
			console.warn('[sessionRefreshUtil] Refresh token expired or invalid');
		} else {
			console.error(
				'[sessionRefreshUtil] Failed to refresh token:',
				response.status,
				response.statusText,
			);
		}
	} catch (error) {
		console.error('[sessionRefreshUtil] Error refreshing token:', error);
	}
}


export function activateSessionRefresh(immediate = false): void {
	if (typeof window === 'undefined') {
		return;
	}

	_setChannel();
	_resetVisibilityListener();

	if (immediate && _shouldRefresh()) {
		if (immediateRefreshTimeout !== null) {
			clearTimeout(immediateRefreshTimeout);
			immediateRefreshTimeout = null;
		}

		const jitter = Math.random() * 4000 + 1000;

		immediateRefreshTimeout = setTimeout(
			() => {
				if (_shouldRefresh()) {
					_refreshToken();
				}

				immediateRefreshTimeout = null;
			},
			jitter,
		);
	}

	_resetInterval();
}


export function deactivateSessionRefresh(): void {
	if (refreshInterval !== null) {
		clearInterval(refreshInterval);
		refreshInterval = null;
	}

	if (immediateRefreshTimeout !== null) {
		clearTimeout(immediateRefreshTimeout);
		immediateRefreshTimeout = null;
	}

	if (visibilityListener !== null) {
		document.removeEventListener('visibilitychange', visibilityListener);
		visibilityListener = null;
	}

	if (channel !== null) {
		channel.close();
		channel = null;
	}
}


export {
	_getLastRefresh,
	_refreshToken,
	_resetInterval,
	_resetVisibilityListener,
	_setChannel,
	setLastRefreshTimestamp,
};

